import { Component, ElementRef, HostListener, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { CreatePostService } from '@core/services/create-post.service';
import { AiCreatePostModalService } from '@features/posts/services/ai-create-post-modal.service';

@Component({
  selector: 'app-step-business-info',
  standalone: true,
  imports: [ReactiveFormsModule],
  templateUrl: './step-business-info.component.html',
  styleUrl: './step-business-info.component.scss',
})
export class StepBusinessInfoComponent {
  protected readonly service = inject(AiCreatePostModalService);
  private readonly fb = inject(FormBuilder);
  private readonly createPostService = inject(CreatePostService);
  private readonly elRef = inject(ElementRef<HTMLElement>);
  isFetchingMetadata = false;
  metadataError = '';
  isGenerateMenuOpen = false;
  readonly quickPrompts = [
    {
      icon: '🚀',
      label: 'Launch announcement',
      prompt:
        'Create a high-converting social media post announcing a new product, service, feature, or launch. Include a strong hook, benefits, excitement, and a compelling CTA.',
    },
    {
      icon: '🔥',
      label: 'Viral engagement post',
      prompt:
        'Write a scroll-stopping social media post designed to boost engagement with curiosity, emotion, and audience interaction.',
    },
    {
      icon: '💡',
      label: 'Educational value post',
      prompt:
        'Create an educational social media post that shares useful tips, insights, or lessons while keeping the content engaging and easy to read.',
    },
    {
      icon: '📖',
      label: 'Storytelling post',
      prompt:
        'Write a storytelling-style social media post that feels authentic, emotional, and relatable while naturally promoting the message or brand.',
    },
    {
      icon: '💰',
      label: 'Promotional offer post',
      prompt:
        'Create a persuasive promotional post for a discount, limited-time offer, sale, or campaign with urgency and a strong call-to-action.',
    },
  ];

  form = this.fb.group({
    prompt: ['', [Validators.required, Validators.minLength(8)]],
    websiteUrl: ['', [Validators.pattern(/https?:\/\/.+/)]],
  });

  get isGenerateDisabled(): boolean {
    return (
      this.form.controls.prompt.invalid ||
      this.isFetchingMetadata ||
      !this.service.hasAiImageCredits()
    );
  }

  fetchWebsiteMetadata() {
    const websiteUrl = this.form.controls.websiteUrl.value?.trim();
    if (!websiteUrl || this.form.controls.websiteUrl.invalid) return;

    this.metadataError = '';
    this.isFetchingMetadata = true;

    this.createPostService
      .extractURLMeta(websiteUrl)
      .pipe(finalize(() => (this.isFetchingMetadata = false)))
      .subscribe({
        next: (response: any) => {
          const businessName =
            response?.default?.title ||
            response?.default?.metaTags?.['og:title'] ||
            response?.default?.site_name ||
            '';
          const businessDescription =
            response?.default?.description ||
            response?.default?.metaTags?.['og:description'] ||
            response?.default?.metaDescription ||
            '';
          this.service.formData.update((fd) => ({
            ...fd,
            businessName: businessName || fd.businessName || '',
            businessDescription: businessDescription || fd.businessDescription || '',
          }));
        },
        error: () => {
          this.metadataError =
            'Unable to fetch website details. You can continue with your prompt.';
        },
      });
  }

  onContinue(
    mode: 'single' | 'three' | 'weekly' = this.service.formData().generationMode || 'three'
  ) {
    if (this.isGenerateDisabled) return;
    const v = this.form.value;
    const prompt = (v.prompt || '').trim();
    const postCount = mode === 'single' ? 1 : mode === 'weekly' ? 5 : 3;
    this.service.formData.update((fd) => ({
      ...fd,
      websiteUrl: v.websiteUrl || '',
      prompt,
      businessName: fd.businessName || '',
      businessDescription: fd.businessDescription || '',
      generationMode: mode,
      postCount,
    }));
    this.isGenerateMenuOpen = false;
    this.service.goToStep(3);
    this.service.generatePosts();
  }

  applyQuickPrompt(text: string) {
    this.form.patchValue({ prompt: text });
    this.form.controls.prompt.markAsTouched();
  }

  toggleGenerateMenu() {
    this.isGenerateMenuOpen = !this.isGenerateMenuOpen;
  }

  get selectedGenerateLabel(): string {
    const mode = this.service.formData().generationMode;
    if (mode === 'single') return 'Generate post';
    if (mode === 'weekly') return 'Generate week posts';
    return 'Generate 3 posts';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event) {
    if (!this.elRef.nativeElement.contains(event.target as Node)) {
      this.isGenerateMenuOpen = false;
    }
  }
}
